import { Spin } from 'antd';
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IProgram } from 'shared/interfaces/IProgram';
import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { Alert } from 'shared/theme/elements';
import {
  Block,
  ConflictChip,
  DayLabel,
  DayRow,
  DAY_H,
  Guide,
  GuideBody,
  GuideFoot,
  Hour,
  HourLine,
  HOUR_W,
  Inner,
  JumpButton,
  JumpGroup,
  Labels,
  LabelsHead,
  Legend,
  Loading,
  Muted,
  NowLine,
  Ruler,
  SchedBar,
  Scroll,
  Spacer,
  Tip,
} from './ProgramSetupSchedule.styled';
import {
  channelOf,
  DAY_MINUTES,
  DAY_NAMES,
  fmtRange,
  isSimulcast,
  nameOf,
  OverlapPair,
  programIdOf,
  ScheduleChange,
  ScheduleMode,
  simulcastOf,
  Slot,
  slotOf,
  SNAP_MINUTES,
  spanOf,
} from './scheduleUtils';

const MIN_BLOCK_W = 16;
const EDGE_ZONE = 70;
const EDGE_STEP = 18;

const JUMPS: { label: string; minute: number | 'now' }[] = [
  { label: 'Now', minute: 'now' },
  { label: 'Morning', minute: 360 },
  { label: 'Noon', minute: 720 },
  { label: 'Evening', minute: 1080 },
  { label: 'Late Night', minute: 1380 },
];

const px = (min: number) => (min / 60) * HOUR_W;
const snap = (min: number) => Math.round(min / SNAP_MINUTES) * SNAP_MINUTES;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const currentMinute = () => {
  const now = new Date();
  return now.getHours() * 60 + now.getMinutes();
};
const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12}${h < 12 ? 'AM' : 'PM'}`;

type DragMode = 'move' | 'resize-l' | 'resize-r';

interface DragState {
  el: HTMLDivElement;
  key: string;
  record: IProgramSetup;
  day: number;
  mode: DragMode;
  slot: Slot;
  x0: number;
  y0: number;
  sl0: number;
  lastX: number;
  lastY: number;
  left0: number;
  w0: number;
  moved: boolean;
  targetDay: number;
}

interface TipState {
  record: IProgramSetup;
  day: number;
  slot: Slot;
  left: number;
  top: number;
  below: boolean;
}

interface Props {
  mode: ScheduleMode;
  records: IProgramSetup[];
  pairs: OverlapPair[];
  programById: Record<string, IProgram>;
  loading?: boolean;
  // Bumped by the parent whenever a proposed change is applied or dropped, so dragged blocks snap to the data.
  resetSignal: number;
  onPropose: (record: IProgramSetup, fromDay: number, next: ScheduleChange) => void;
  onEdit: (id: string) => void;
}

/** Where a finished drag lands, in minutes. Overnight airings are handled on an unwrapped clock. */
const resolveDrop = (d: DragState, dMin: number): ScheduleChange => {
  const { start, end } = d.slot;
  const endU = end <= start ? end + DAY_MINUTES : end;
  const wrapMin = (m: number) => ((m % DAY_MINUTES) + DAY_MINUTES) % DAY_MINUTES;

  if (d.mode === 'move') {
    const dur = endU - start;
    const ns = clamp(start + dMin, 0, Math.max(0, DAY_MINUTES - dur));
    return { day: d.targetDay, start: ns, end: wrapMin(ns + dur) };
  }
  if (d.mode === 'resize-r') {
    const ne = clamp(endU + dMin, start + SNAP_MINUTES, start + DAY_MINUTES - SNAP_MINUTES);
    return { day: d.day, start, end: wrapMin(ne) };
  }
  const ns = clamp(start + dMin, endU - DAY_MINUTES + SNAP_MINUTES, endU - SNAP_MINUTES);
  return { day: d.day, start: wrapMin(ns), end };
};

export const ScheduleGuide = ({
  mode,
  records,
  pairs,
  programById,
  loading,
  resetSignal,
  onPropose,
  onEdit,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const panRef = useRef<{ x: number; sl: number } | null>(null);
  const edgeTimer = useRef<number | null>(null);
  const onProposeRef = useRef(onPropose);
  onProposeRef.current = onPropose;
  const onEditRef = useRef(onEdit);
  onEditRef.current = onEdit;

  const [epoch, setEpoch] = useState(0);
  const [panning, setPanning] = useState(false);
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dropDay, setDropDay] = useState<number | null>(null);
  const [tip, setTip] = useState<TipState | null>(null);
  const [nowMin, setNowMin] = useState(currentMinute);

  // Remount blocks (dropping any inline drag styles) when the data or the pending change resets.
  useEffect(() => {
    setEpoch((e) => e + 1);
  }, [resetSignal, records]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMin(currentMinute()), 60 * 1000);
    return () => window.clearInterval(timer);
  }, []);

  const conflictSet = useMemo(() => {
    const set = new Set<string>();
    pairs.forEach(({ day, a, b }) => {
      set.add(`${a._id}|${day}`);
      set.add(`${b._id}|${day}`);
    });
    return set;
  }, [pairs]);

  const scrollToMinute = useCallback((minute: number) => {
    const sc = scrollRef.current;
    if (sc) sc.scrollLeft = Math.max(0, px(minute) - sc.clientWidth / 2 + 56);
  }, []);

  useEffect(() => {
    scrollToMinute(currentMinute());
  }, [scrollToMinute]);

  /* ---------- drag to move / resize, drag empty space to pan ---------- */
  useEffect(() => {
    const scrollLeft = () => scrollRef.current?.scrollLeft ?? 0;
    const dragDx = (d: DragState) => d.lastX - d.x0 + (scrollLeft() - d.sl0);

    const updateDrag = () => {
      const d = dragRef.current;
      if (!d) return;
      const dx = dragDx(d);
      const dy = d.lastY - d.y0;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) d.moved = true;
      const dPx = px(snap((dx / HOUR_W) * 60));
      if (d.mode === 'move') {
        d.el.style.left = `${d.left0 + dPx}px`;
        d.targetDay = clamp(d.day + Math.round(dy / DAY_H), 0, 6);
        setDropDay(d.targetDay !== d.day ? d.targetDay : null);
      } else if (d.mode === 'resize-r') {
        d.el.style.width = `${Math.max(MIN_BLOCK_W, d.w0 + dPx)}px`;
      } else {
        const nl = Math.min(d.left0 + dPx, d.left0 + d.w0 - MIN_BLOCK_W);
        d.el.style.left = `${nl}px`;
        d.el.style.width = `${d.w0 - (nl - d.left0)}px`;
      }
    };

    const stopEdge = () => {
      if (edgeTimer.current !== null) {
        window.clearInterval(edgeTimer.current);
        edgeTimer.current = null;
      }
    };
    // Near an edge the timeline scrolls itself, so a block can be dropped at any hour.
    const edgeScroll = () => {
      const d = dragRef.current;
      const sc = scrollRef.current;
      if (!d || !sc) {
        stopEdge();
        return;
      }
      const r = sc.getBoundingClientRect();
      let v = 0;
      if (d.lastX < r.left + EDGE_ZONE) v = -EDGE_STEP;
      else if (d.lastX > r.right - EDGE_ZONE) v = EDGE_STEP;
      if (v) {
        const before = sc.scrollLeft;
        sc.scrollLeft += v;
        if (sc.scrollLeft !== before) updateDrag();
      }
    };

    const onMove = (e: MouseEvent) => {
      const pan = panRef.current;
      if (pan && scrollRef.current) {
        scrollRef.current.scrollLeft = pan.sl - (e.clientX - pan.x);
        return;
      }
      const d = dragRef.current;
      if (!d) return;
      d.lastX = e.clientX;
      d.lastY = e.clientY;
      if (edgeTimer.current === null) edgeTimer.current = window.setInterval(edgeScroll, 16);
      updateDrag();
    };

    const onUp = (e: MouseEvent) => {
      if (panRef.current) {
        panRef.current = null;
        setPanning(false);
        return;
      }
      const d = dragRef.current;
      if (!d) return;
      dragRef.current = null;
      stopEdge();
      setDraggingKey(null);
      setDropDay(null);
      // A press without a drag is a click: open the program.
      if (!d.moved) {
        setEpoch((n) => n + 1);
        onEditRef.current(d.record._id);
        return;
      }
      d.lastX = e.clientX;
      const next = resolveDrop(d, snap((dragDx(d) / HOUR_W) * 60));
      if (next.start === next.end) {
        setEpoch((n) => n + 1);
        Alert.error('A program needs a start and an end that differ');
        return;
      }
      if (next.day === d.day && next.start === d.slot.start && next.end === d.slot.end) {
        setEpoch((n) => n + 1);
        return;
      }
      // The block stays where it was dropped until the change is applied or cancelled.
      onProposeRef.current(d.record, d.day, next);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      stopEdge();
    };
  }, []);

  const handlePanStart = (e: ReactMouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-block]')) return; // blocks keep their own drag
    panRef.current = { x: e.clientX, sl: scrollRef.current?.scrollLeft ?? 0 };
    setPanning(true);
    e.preventDefault();
  };

  const handleBlockDown = (
    e: ReactMouseEvent<HTMLDivElement>,
    key: string,
    record: IProgramSetup,
    day: number,
    slot: Slot,
  ) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    const el = e.currentTarget;
    let dragMode: DragMode = 'move';
    if (target.classList.contains('hdl')) dragMode = target.classList.contains('l') ? 'resize-l' : 'resize-r';
    dragRef.current = {
      el,
      key,
      record,
      day,
      mode: dragMode,
      slot,
      x0: e.clientX,
      y0: e.clientY,
      sl0: scrollRef.current?.scrollLeft ?? 0,
      lastX: e.clientX,
      lastY: e.clientY,
      left0: parseFloat(el.style.left),
      w0: parseFloat(el.style.width),
      moved: false,
      targetDay: day,
    };
    setDraggingKey(key);
    setTip(null);
    e.preventDefault();
    e.stopPropagation();
  };

  const showTip = (e: ReactMouseEvent<HTMLDivElement>, record: IProgramSetup, day: number, slot: Slot) => {
    if (dragRef.current || panRef.current) return;
    const b = e.currentTarget.getBoundingClientRect();
    const below = b.top < 150;
    setTip({
      record,
      day,
      slot,
      left: clamp(b.left, 8, window.innerWidth - 278),
      top: below ? b.bottom + 8 : b.top - 8,
      below,
    });
  };

  const handleConflictChip = () => {
    const first = pairs[0];
    if (!first) return;
    const slot = slotOf(first.b, first.day);
    if (slot) scrollToMinute(slot.start);
  };

  const dayCounts = useMemo(
    () => DAY_NAMES.map((_, i) => records.filter((record) => !!slotOf(record, i)).length),
    [records],
  );

  const genre = tip ? programById[programIdOf(tip.record, mode) || '']?.program_type_label : undefined;

  return (
    <>
      <SchedBar>
        <JumpGroup>
          {JUMPS.map(({ label, minute }) => (
            <JumpButton
              key={label}
              type="button"
              $now={minute === 'now'}
              onClick={() => scrollToMinute(minute === 'now' ? currentMinute() : minute)}
            >
              {label}
            </JumpButton>
          ))}
        </JumpGroup>
        <Spacer />
        {pairs.length ? (
          <ConflictChip type="button" onClick={handleConflictChip} title="Jump to the first overlap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 8v5M12 16.5v.5" />
              <circle cx="12" cy="12" r="9" />
            </svg>
            {pairs.length} schedule conflict{pairs.length === 1 ? '' : 's'}
          </ConflictChip>
        ) : (
          <ConflictChip type="button" $clean>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m5 12 5 5L20 7" />
            </svg>
            No schedule conflicts
          </ConflictChip>
        )}
      </SchedBar>

      <Guide>
        {loading && (
          <Loading>
            <Spin />
          </Loading>
        )}
        <GuideBody>
          <Labels>
            <LabelsHead />
            {DAY_NAMES.map((name, i) => (
              <DayLabel key={name} $alt={i >= 5}>
                <div>
                  {name.slice(0, 3)}
                  <small>
                    {dayCounts[i]} program{dayCounts[i] === 1 ? '' : 's'}
                  </small>
                </div>
              </DayLabel>
            ))}
          </Labels>
          <Scroll
            ref={scrollRef}
            $panning={panning}
            onMouseDown={handlePanStart}
            onScroll={() => tip && setTip(null)}
            role="presentation"
          >
            <Inner>
              <Ruler>
                {Array.from({ length: 24 }, (_, h) => (
                  <Hour key={h}>{hourLabel(h)}</Hour>
                ))}
              </Ruler>
              {DAY_NAMES.map((name, day) => (
                <DayRow key={name} $alt={day >= 5} $drop={dropDay === day}>
                  {Array.from({ length: 23 }, (_, h) => (
                    <HourLine key={h} style={{ left: (h + 1) * HOUR_W }} />
                  ))}
                  {records.map((record) => {
                    const slot = slotOf(record, day);
                    if (!slot) return null;
                    const segs = spanOf(slot);
                    let variant: 'ok' | 'conflict' | 'sim' = 'ok';
                    if (conflictSet.has(`${record._id}|${day}`)) variant = 'conflict';
                    else if (isSimulcast(record, mode)) variant = 'sim';
                    const programName = nameOf(record, mode);
                    return segs.map(([from, to], k) => {
                      const key = `${record._id}|${day}|${k}`;
                      return (
                        <Block
                          key={`${key}|${epoch}`}
                          data-block
                          role="button"
                          tabIndex={0}
                          aria-label={`${programName}, ${DAY_NAMES[day]}, ${fmtRange(slot)}`}
                          $variant={variant}
                          $dragging={draggingKey === key}
                          style={{ left: px(from), width: Math.max(MIN_BLOCK_W, px(to - from)) }}
                          onMouseDown={(e) => handleBlockDown(e, key, record, day, slot)}
                          onMouseEnter={(e) => showTip(e, record, day, slot)}
                          onMouseLeave={() => setTip(null)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') onEdit(record._id);
                          }}
                        >
                          {segs.length > 1 && <span className="cont">{k === 0 ? '→' : '↵'}</span>}
                          <div className="bt">{programName}</div>
                          <div className="bs ps-mono">{fmtRange(slot)}</div>
                          <div className="hdl l" />
                          <div className="hdl r" />
                        </Block>
                      );
                    });
                  })}
                </DayRow>
              ))}
              <NowLine style={{ left: px(nowMin) }} />
            </Inner>
          </Scroll>
        </GuideBody>
        <GuideFoot>
          <Legend $variant="ok">
            <i />
            Scheduled
          </Legend>
          <Legend $variant="conflict">
            <i />
            Overlapping on this {mode === 'tv' ? 'channel' : 'station'}
          </Legend>
          <Legend $variant="sim">
            <i />
            Simulcast
          </Legend>
          <Muted>
            Drag a block to move it · drag an edge to resize · drag empty space to pan the day · click to open
          </Muted>
        </GuideFoot>
      </Guide>

      {tip && (
        <Tip style={{ left: tip.left, top: tip.top, transform: tip.below ? undefined : 'translateY(-100%)' }}>
          <div className="n">{nameOf(tip.record, mode)}</div>
          <div className="r">
            <span>{mode === 'tv' ? 'Channel' : 'Station'}</span>
            <span>{channelOf(tip.record, mode)}</span>
          </div>
          <div className="r">
            <span>Day</span>
            <span>{DAY_NAMES[tip.day]}</span>
          </div>
          <div className="r">
            <span>Schedule</span>
            <span>{fmtRange(tip.slot)}</span>
          </div>
          {genre && (
            <div className="r">
              <span>Genre</span>
              <span>{genre}</span>
            </div>
          )}
          {isSimulcast(tip.record, mode) && (
            <div className="r">
              <span>Simulcast</span>
              <span>{simulcastOf(tip.record, mode)}</span>
            </div>
          )}
        </Tip>
      )}
    </>
  );
};
